import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { SesionService } from '@intelishs-group/session-core';

/**
 * Deja pasar si el acceso activo tiene ALGUNO de estos módulos.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * POR QUÉ POR MÓDULO Y NO POR ROL
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Sustituye a `roleGuard(['Admin','RH',…])`, que comparaba el rol del token
 * contra una tabla de NUEVE nombres (`'administrador rh'`, `'mi portal rh'`,
 * `'administrador nomina'`…) traducidos a cuatro canónicos. Esa tabla existía
 * porque el nombre del rol es texto libre que escribe un administrador, así que
 * cada ambiente lo escribía distinto y había que recogerlos todos.
 *
 * El módulo otorgado es un HECHO que emite identidad, no una convención de
 * nombres: el código de un rol sólo identifica dentro de su aplicación, pero
 * `empleados` es `empleados` en todas partes. Si mañana alguien renombra
 * «Administrador RH» a «Jefatura de Personal», esto sigue funcionando y la tabla
 * de traducción habría dejado a esa persona fuera sin un solo error.
 *
 * ⚠️ El superadministrador pasa SIN tener el módulo, y es correcto: su acceso es
 * derivado, así que su rol tiene CERO módulos otorgados. Lo resuelve
 * `tieneModulo()` en la librería, no este guard — si aquí se hiciera
 * `modulos().some(...)` a secas, se le negaría todo.
 *
 * ── Por qué acepta VARIOS códigos ─────────────────────────────────────────
 *
 * Por un caso real y uno solo: `empleados/detalle/:id`. Hoy lo ve un Director,
 * que NO tiene el módulo `empleados` —ése es de Administración— pero sí
 * `mis-autorizaciones`, porque necesita abrir la ficha de quien le pide
 * vacaciones. Con un único código, ese director se quedaría fuera de una
 * pantalla a la que hoy entra, y la migración habría quitado un permiso sin que
 * nadie lo pidiera.
 *
 * No es la puerta de atrás para «pedir menos»: cada ruta declara EL SUYO, y las
 * listas de dos son la excepción documentada.
 */
export function moduloGuard(...codigos: readonly string[]): CanActivateFn {
  return () => {
    const sesion = inject(SesionService);
    if (codigos.some((codigo) => sesion.tieneModulo(codigo))) return true;

    // A la bienvenida y no al login: hay sesión, lo que falta es el permiso.
    // Mandarlo al login le pediría volver a entrar para quedarse igual.
    return inject(Router).createUrlTree(['/bienvenida']);
  };
}
